import { computed, ref } from '@nuxtjs/composition-api';
import useEnvironment from '~/composable/various/useEnvironment';
import useTlmCart from '~/composable/useTlmCart';
import useCheckout from '~/composable/checkout/useCheckout';
import { ACTION_STATUS } from '~/constants/ActionStatus';
import { CHECKOUT_FLOW } from '~/constants/CheckoutFlow';

export default function useMitID (appContext) {
  const notification = ref('');
  const userData = ref({});
  const cprAddressData = ref({});
  const hubbleClient = appContext.$hubble;
  const { isDevEnv } = useEnvironment();
  if (!appContext || !hubbleClient) {
    throw new Error('You must provide the nuxt app context to use this composable');
  }
  const { updateCart, getCartID, isProtectedAddress } = useTlmCart(appContext);
  const generateMitIDUrl = (mitIdRedirectUrlParams) => {
    const params = new URLSearchParams(mitIdRedirectUrlParams).toString();
    const redirectUrl = `${window.location.origin}${window.location.pathname}?${params}`;
    let baseUrl = window.location.protocol + '//' + window.location.hostname + '/gw';

    if (isDevEnv()) {
      baseUrl = 'http://localhost:7001';
    }
    notification.value = `Generated MitID url = ${baseUrl}/HubbleAuthAPI/api/public/
    mitid/authorize?telmoreRedirectUrl=${redirectUrl}`
    return `${baseUrl}/HubbleAuthAPI/api/public/mitid/authorize?telmoreRedirectUrl=${redirectUrl}`;
  }
  const getMitIdToken = () => {
    let accessToken = null;
    if (process.client) {
      accessToken = JSON.parse(window.sessionStorage.getItem('mitIdToken'))?.encryptedAccessToken
    }
    return accessToken;
  }
  const startMitIDFlow = (mitIdRedirectUrlParams) => {
    if (process.client) {
      const mitIDUrl = generateMitIDUrl(mitIdRedirectUrlParams);
      notification.value = 'Starting mitID flow.....';
      window.location.href = mitIDUrl;
    } else {
      console.warn('composeable useMitID.startMitIDFlow is used serverside, does not work ')
    }
  }
  const validateMitIdTokenExist = () => {
    let hasAccessToken = false;
    if (process.client) {
      hasAccessToken = window.sessionStorage.getItem('mitIdToken')
    }
    notification.value = hasAccessToken ? 'MitID token exist' : 'No Valid MitIdToken could be found';
    return hasAccessToken;
  }
  const removeNotifications = () => {
    notification.value = ''
  }
  const fetchToken = async (code) => {
    notification.value = 'Fetching mitid access token...!!!';
    if (!code) {
      notification.value = 'no code exists';
      return;
    }
    try {
      const token = await hubbleClient.mitID.fetchToken(code);
      notification.value = 'Mitid access token fetched...!!!';

      if (!token.data) {
        notification.value = 'Error fetching token';
      } else {
        window.sessionStorage.setItem('mitIdToken', JSON.stringify(token.data));
        return token.data;
      }
    } catch (e) {
      window.sessionStorage.removeItem('mitIdToken');
      notification.value = 'Could not get token';
    }
  }
  const removeToken = () => {
    window.sessionStorage.removeItem('mitIdToken');
  }
  const getUserInfo = async () => {
    notification.value = 'fetching user details...!!!';
    const mitIdToken = window.sessionStorage.getItem('mitIdToken');
    if (!mitIdToken) {
      throw new Error('There must be a vaild mitid token to get information');
    }
    const encryptedAccessToken = JSON.parse(mitIdToken)?.encryptedAccessToken;
    try {
      notification.value = 'Getting user details from mitid';
      const response = await hubbleClient.mitID.getUserInfo(encryptedAccessToken);
      notification.value = 'user details fetched successfully';
      userData.value = response.data;
      return response.data;
    } catch (e) {
      notification.value = 'Error fetching user details...!!!';
      window.sessionStorage.removeItem('mitIdToken');
      userData.value = {};
    } finally { /* empty */ }
  }
  const getCprDataByAccessToken = async () => {
    notification.value = 'Getting addressInfo...!!!';
    const mitIdToken = window.sessionStorage.getItem('mitIdToken');
    if (!mitIdToken) {
      throw new Error('There ,must be a valid mitid token to get information');
    }
    const encryptedAccessToken = JSON.parse(mitIdToken)?.encryptedAccessToken;
    try {
      notification.value = 'Getting cpr data from cpr reg using mitid access token';
      const response = await hubbleClient.mitID.getCprInfoUsingMitIdAccessToken(encryptedAccessToken);
      notification.value = 'Address data  fetched successfully';
      cprAddressData.value = response.data;
      return response.data;
    } catch (e) {
      notification.value = 'Error fetching cpr data...!!!';
      window.sessionStorage.removeItem('mitIdToken');
      userData.value = {};
      cprAddressData.value = {};
    } finally { /* empty */ }
  }
  const addAddressInformationToCartByAccessToken = async () => {
    notification.value = 'Getting addressInfo and adding information to cart...!!!';
    const mitIdToken = window.sessionStorage.getItem('mitIdToken');
    if (!mitIdToken) {
      throw new Error('There must be a vaild mitid token to get information');
    }
    const encryptedAccessToken = JSON.parse(mitIdToken)?.encryptedAccessToken;
    const { checkoutType } = useCheckout(appContext);
    const skipCprInCart = checkoutType?.value === CHECKOUT_FLOW.WITH_OUT_SUBSCRIPTION;
    try {
      const response = await hubbleClient.mitID.addAddressInformationToCartByAccessToken(getCartID.value, encryptedAccessToken, skipCprInCart);
      if (response.data.actionStatus === ACTION_STATUS.OK) {
        updateCart(response.data.responseObject.shoppingCart);
        isProtectedAddress.value = response.data.responseObject.protectedAddress
        // updateAddressInCustomerInfo(response.data.responseObject.billingAddress, userData.value)
        notification.value = 'Address data fetched successfully and added to cart';
      } else {
        notification.value = 'Address data not added successfully';
      }
      return response.data;
    } catch (e) {
      notification.value = 'Error fetching cpr data and updating cart...!!!';
      window.sessionStorage.removeItem('mitIdToken');
      userData.value = {};
      cprAddressData.value = {};
    } finally { /* empty */ }
  }
  return {
    removeToken,
    getMitIdToken,
    startMitIDFlow,
    fetchToken,
    getUserInfo,
    generateMitIDUrl,
    notification,
    validateMitIdTokenExist,
    removeNotifications,
    getCprDataByAccessToken,
    addAddressInformationToCartByAccessToken,
    cprAddressData: computed(() => Object.keys(cprAddressData.value).length ? cprAddressData.value : ''),
    userData: computed(() => Object.keys(userData.value).length ? userData.value : '')
  }
}
